import type { ComponentProps, ReactElement } from 'react';
import ReactMarkdown, { type ExtraProps } from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { ArticleOverflow } from './ArticleOverflow';
import 'katex/dist/katex.min.css';

// Sanitize authored HTML before KaTeX: its generated styles, SVG roots and MathML
// are essential to typesetting. KaTeX's trust:false keeps HTML/URL commands off.
// https://github.com/rehypejs/rehype-sanitize#example-math
const sanitizeSchema: NonNullable<Parameters<typeof rehypeSanitize>[0]> = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [['className', /^language-./, 'math-inline', 'math-display']],
    div: [...(defaultSchema.attributes?.div ?? []), ['className', 'structura-bit-badge', 'key-takeaways', 'info-box']],
  },
};

interface ArticleMarkdownProps {
  content: string;
  labels: { equation: string; table: string; scrollHint: string };
}

export function ArticleMarkdown({ content, labels }: ArticleMarkdownProps): ReactElement {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema], [rehypeKatex, { trust: false, output: 'htmlAndMathml' }]]}
      components={{
        span: ({ node: _node, className, children, ...props }: ComponentProps<'span'> & ExtraProps): ReactElement => className === 'katex-display' ? (
          <ArticleOverflow kind="equation" label={labels.equation} hint={labels.scrollHint}>
            <span className={className} {...props}>{children}</span>
          </ArticleOverflow>
        ) : <span className={className} {...props}>{children}</span>,
        img: ({ node: _node, src, alt, ...props }: ComponentProps<'img'> & ExtraProps): ReactElement => <img {...props} src={src?.startsWith('./images/') ? `/api/StructuraBits/images/${src.split('/').pop()}` : src} alt={alt ?? ''} loading="lazy" decoding="async" />,
        code: ({ node: _node, className, ...props }: ComponentProps<'code'> & ExtraProps): ReactElement => <code className={className ?? 'inline-code'} {...props} />,
        blockquote: ({ node: _node, ...props }: ComponentProps<'blockquote'> & ExtraProps): ReactElement => <blockquote className="article-blockquote" {...props} />,
        table: ({ node: _node, ...props }: ComponentProps<'table'> & ExtraProps): ReactElement => (
          <ArticleOverflow kind="table" label={labels.table} hint={labels.scrollHint}>
            <table className="article-table" {...props} />
          </ArticleOverflow>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
}
